import React, { useState } from 'react';
import { Row, Col, Typography, Divider, Modal, Tag, Button } from 'antd';
import { LinkedinOutlined, FacebookOutlined, InstagramOutlined, BuildOutlined, PhoneOutlined, MailOutlined, QuestionCircleOutlined, XOutlined } from '@ant-design/icons';
import PartnerEnquiryForm from '../common/PartnerEnquiryForm';

const { Title, Text } = Typography;

interface Partner {
  id: number;
  name: string;
  location: string; // e.g., "Kolkata"
  type: string;
  address: string;
  website: string;
  contactPerson: string;
  email: string;
  phone: string;
  icon: React.ReactNode;
}

interface PartnerRegion {
  regionName: string;
  partners: Partner[];
}

const Footer: React.FC = () => {
  const [partnerModalVisible, setPartnerModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [enquiryModalVisible, setEnquiryModalVisible] = useState(false);

  // Organized by regions - scalable structure for future additions
  const partnerRegions: PartnerRegion[] = [
    {
      regionName: 'Eastern & North Eastern Region',
      partners: [
        {
          id: 1,
          name: 'Krit Consultancy',
          location: 'Kolkata',
          type: 'Consultancy',
          address: 'Mani Casadona Office Unit-5WS2D, West Tower Plot No: II F/04, Action Area: II-F Rajarhat Newtown, Kolkata 700156.',
          website: 'https://kritconsultancy.com',
          contactPerson: 'Kanchan Thakur',
          email: 'hr@kritconsultancy.com',
          phone: '+91 7003999359',
          icon: <BuildOutlined style={{ fontSize: '32px', color: '#1890ff' }} />
        }
      ]
    }
    // Future regions can be added here, e.g.:
    // {
    //   regionName: 'Western Region',
    //   partners: [...]
    // }
  ];

  const handlePartnerClick = (partner: Partner) => {
    setSelectedPartner(partner);
    setPartnerModalVisible(true);
  };


  return (
    <>
      <footer className="site-footer">
        <div className="footer-content">
          <Row gutter={[48, 32]}>
            <Col xs={24} sm={12} md={8}>
              <div className="footer-section">
                <Title level={4}>About enpropeL</Title>
                <p>Empowering students with opportunities for growth, learning, and professional development through our comprehensive platform.</p>
                <div className="social-links">
                  <a href="https://www.linkedin.com/company/112104398/admin/dashboard" target="_blank" rel="noopener noreferrer"><LinkedinOutlined /></a>
                  <a href="https://x.com/enpropeL" target="_blank" rel="noopener noreferrer"><XOutlined /></a>
                  <a href="https://www.instagram.com/enpropel" target="_blank" rel="noopener noreferrer"><InstagramOutlined /></a>
                  <a href="https://www.facebook.com/people/Enprope-L/pfbid02FxjQErzA2d95WGdsFMss4LU8u5x7Fmb85cVf5sE8RLhYsHSFSwpHPngMSRGqjVitl" target="_blank" rel="noopener noreferrer"><FacebookOutlined /></a>
                </div>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="footer-section">
                <Title level={4}>Quick Links</Title>
                <ul className="footer-links">
                  <li><a href="/about-us">About Us</a></li>
                  <li><a href="/contact-us">Contact Us</a></li>
                  <li style={{ marginTop: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 'bold' }}>Our Associate Partners:</span>
                      <Button
                        type="primary"
                        size="small"
                        icon={<QuestionCircleOutlined />}
                        onClick={() => setEnquiryModalVisible(true)}
                      >
                        Partner Enquiry
                      </Button>
                    </div>
                  </li>
                  {partnerRegions.map((region, regionIndex) => (
                    <li key={regionIndex} style={{ marginTop: '8px' }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '12px', paddingLeft: '15px', color: 'rgb(188 198 208)' }}>
                        {region.regionName}
                      </div>
                      <ul style={{ marginLeft: '16px', marginTop: '4px', listStyle: 'none', padding: 0 }}>
                        {region.partners.map(partner => (
                          <li key={partner.id} style={{ marginBottom: '4px' }}>
                            <span
                              className="partner-type"
                              onClick={() => handlePartnerClick(partner)}
                              style={{ cursor: 'pointer', color: '#666', textDecoration: 'underline' }}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handlePartnerClick(partner);
                                }
                              }}
                            >
                              {partner.name}, {partner.location}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
            </Col>
          </Row>
        </div>
        <Divider className="footer-divider" />
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} enpropeL. All rights reserved.</p>
        </div>
      </footer>

      {/* Partner Details Modal */}
      <Modal
        title=""
        open={partnerModalVisible}
        onCancel={() => setPartnerModalVisible(false)}
        footer={null}
        width={500}
        centered
      >
        {selectedPartner && (
          <div className="university-content">
            <div className="university-header">
              <div className="university-icon">
                {selectedPartner.icon}
              </div>
              <div className="university-info">
                <Title level={4} className="university-name">{selectedPartner.name}</Title>
                <Tag className="university-type">{selectedPartner.type}</Tag>
              </div>
            </div>

            <div className="university-details">
              <div className="university-location">
                <Text strong>Address: </Text><br />
                <Text>{selectedPartner.address}</Text>
              </div>
              <div className="university-stats">
                <div className="university-item">
                  <Text strong>Website: </Text><br />
                  <Text><a href={`${selectedPartner.website}`} target="_blank" rel="noopener noreferrer">{selectedPartner.website}</a></Text>
                </div>
                <div className="university-item">
                  <Text strong>Contact Person: </Text><br />
                  <Text>{selectedPartner.contactPerson}</Text>
                </div>
              </div>

              <div className="university-programs">
                <Text strong>Contact Details:</Text>
                <div className="program-tags">
                  <Tag className="program-tag"><a href={`mailto:${selectedPartner.email}`}> <MailOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} /> {selectedPartner.email}</a></Tag>
                  <Tag className="program-tag"><a href={`tel:${selectedPartner.phone.replace(/[^0-9+]/g, '')}`}> <PhoneOutlined style={{ marginRight: '4px', color: '#8c8c8c' }} /> {selectedPartner.phone}</a></Tag>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Partner Enquiry Modal */}
      <PartnerEnquiryForm
        visible={enquiryModalVisible}
        onCancel={() => setEnquiryModalVisible(false)}
      />
    </>
  );
};

export default Footer;
